import React, {
	useState, useEffect, FormEvent, KeyboardEvent, useRef,
} from 'react';
import TooltipWrapper from './tooltips/tooltip-wrapper';
import { getVideoTimeAsHHMMSS } from '../../utils/utils';

interface DurationProps {
	getDurationCB: Function;
	initialDuration?: number | null; // shotDuration or distributed Segment duration
	rawDuration?: number | null; // stored shotDuration value
	isInputDisabled?: boolean;
	tooltipMessage?: string;
	allowZero?: boolean;
	maxDuration?: number;
	minDuration?: number;
}

export enum DURATION_DEFAULTS {
	SECONDS=0,
	MINUTES=1,
	HOURS=2,
}

export default function DurationPicker({
	initialDuration,
	rawDuration,
	getDurationCB,
	isInputDisabled = false,
	tooltipMessage,
	allowZero = false,
	maxDuration,
	minDuration,
}: DurationProps) {
	const [completeString, setCompleteString] = useState<string>(initialDuration?.toString() || '');

	const durationRef = useRef<HTMLInputElement | null>(null);

	const updateDurationText = (duration: number | null | undefined) => {
		const newString = duration ? getVideoTimeAsHHMMSS(duration) : "0:00";
		setCompleteString(duration || allowZero ? newString : '');
	};

	useEffect(() => {
		updateDurationText(initialDuration);
	}, [initialDuration]);

	const pickerRef = useRef<HTMLInputElement | null>(null);

	const getDurationsFromString = (newDuration: string) => {
		let seconds = '';
		let minutes = '';
		let hours = '';
		const secondsMinutesHours = newDuration.split(':');
		secondsMinutesHours.reverse();
		if (secondsMinutesHours.length === 1) {
			seconds = '';
			minutes = secondsMinutesHours[0] ? secondsMinutesHours[0] : '';
			hours = '';
		} else {
			seconds = secondsMinutesHours[0] ? secondsMinutesHours[0] : '';
			minutes = secondsMinutesHours[1] ? secondsMinutesHours[1] : '';
			hours = secondsMinutesHours[2] ? secondsMinutesHours[2] : '';
		}

		return { seconds, minutes, hours };
	};

	const changeShotDuration = (e?: FormEvent<HTMLInputElement>) => {
		const currentTarget = e?.currentTarget;
		const newDuration = pickerRef.current?.value.replace(/[^0-9:]/g, '') ?? '';

		const {
			seconds,
			minutes,
			hours,
		} = getDurationsFromString(newDuration);

		let newShotDurationInSeconds = (Number(hours) * 60 * 60)
			+ (Number(minutes) * 60)
			+ Number(seconds);

		// Clamp to maxDuration if provided
		if (maxDuration !== undefined && newShotDurationInSeconds > maxDuration) {
			newShotDurationInSeconds = maxDuration;
		}
		if (minDuration !== undefined && newShotDurationInSeconds < minDuration) {
			newShotDurationInSeconds = minDuration;
		}

		setTimeout(() => {
			if (document.activeElement !== currentTarget) {
				const comparisonDuration = rawDuration !== undefined ? rawDuration : initialDuration;
				if (newShotDurationInSeconds !== comparisonDuration) {
					getDurationCB(newShotDurationInSeconds);
				} else {
					updateDurationText(initialDuration ?? newShotDurationInSeconds);
				}
			}
		}, 0);
	};

	const updateValue = (e:FormEvent<HTMLInputElement>) => {
		const newDuration = e.currentTarget.value.replace(/[^0-9:]/g, '');
		setCompleteString(newDuration);
	};

	return (
		<TooltipWrapper message={tooltipMessage}>
			<div className="duration-container">
				<div className="duration-picker"
					ref={durationRef}
				>
					{!isInputDisabled ? (
						<input
							data-testid="duration-picker"
							type="text"
							pattern="[0-9:]+"
							onKeyDown={(e:KeyboardEvent<HTMLInputElement>) => {
								const count = (completeString.match(/:/g) || []).length;
								if (e.code === 'Semicolon' && count > 1) {
									e.preventDefault();
								} else if (e.key === 'Enter') {
									changeShotDuration(e as any);
									setTimeout(() => {
										(e.target as HTMLInputElement)?.blur();
									}, 250);
									e.preventDefault();
								}
							}}
							className='duration-picker bl-font-p2'
							onBlur={changeShotDuration}
							onChange={updateValue}
							value={completeString}
							maxLength={8}
							placeholder={'None'}
							ref={pickerRef}
						/>
					) : (
						<input
							data-testid="duration-picker"
							className="duration-picker bl-font-p2"
							type="text"
							value={completeString}
							disabled={true}
						/>
					)}
				</div>
			</div>
		</TooltipWrapper>
	);
}
